import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormControl, InputLabel, TextField, MenuItem, Select } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FormHelperText, Grid, Typography } from "@financeable-com-au/financeable-ui";
import * as Yup from "yup";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import OrganisationAddAclSection, {
  getAclPayload,
  INITIAL_ACL_FIELD_ERRORS,
  INITIAL_ACL_INFO,
} from "./OrganisationAddAclSection";
import OrganisationSearch from "../Utils/OrganisationSearch";
import regex from "../../utils/regex";
import {
  createOrganisation,
  getAllParentOrganisation,
  getOrganisation,
  organisationSelector,
  updateOrganisation,
} from "../../store/slices/organisationSlice";
import { ERRORS_MESSAGE, SUCCESS_MESSAGE } from "../../constants";
import { useAccessAppComponent } from "../../hooks/useAccessAppComponent";
import { Loader } from "../Loader";

const INITIAL_ORG_INFO = {
  name: "",
  abn: "",
  acn: "",
  phoneNumber: "",
  telephoneNumber: "",
  email: "",
  complaintsEmail: "",
  serviceInvoicePaidBy: "",
  parentOrganisation: "",
  address: "",
};

const optionalString = (re, message) =>
  Yup.string()
    .optional()
    .test("match", message, (v) => !v || re.test(String(v).trim()));

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Organisation Name is required.")
    .matches(regex.alphaNumericAndSpecificCharOptional, "Not a valid Organisation name."),
  abn: Yup.string().required("Not a valid ABN.").matches(regex.ABN, "Not a valid ABN."),
  acn: optionalString(regex.ACN, "Not a valid ACN."),
  serviceInvoicePaidBy: Yup.string().required("Invoice Paid by is required."),
  phoneNumber: optionalString(regex.phoneNumber, "Not a valid Australia mobile phone number."),
  telephoneNumber: optionalString(regex.telephoneNumber, "Not a valid telephone number."),
  email: optionalString(regex.email, "Not a valid email address."),
  complaintsEmail: optionalString(regex.email, "Not a valid email address."),
  address: optionalString(regex.addressRegex, "Not a valid address."),
});

const OrganisationAdd = () => {
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { accessComponents } = useAccessAppComponent();
  const orgAccessComponent = accessComponents.organisation;

  const { parentOrganisationList, gettingOrganisation } = useSelector(organisationSelector);
  const [organisationDetails, setOrganisationDetails] = useState(null);
  const [organisationInfo, setOrganisationInfo] = useState(INITIAL_ORG_INFO);
  const [fieldErrors, setFieldErrors] = useState({});
  const [aclInfo, setAclInfo] = useState(INITIAL_ACL_INFO);
  const [aclFieldErrors, setAclFieldErrors] = useState(INITIAL_ACL_FIELD_ERRORS);

  const organisationNameList = parentOrganisationList?.map((organisation) => ({
    name: organisation?.name,
    orgId: organisation?._id,
  }));

  const validateField = useCallback(async (fieldName, value) => {
    try {
      await validationSchema.validateAt(fieldName, { [fieldName]: value ?? "" });
      setFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
    } catch (error) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error.message }));
    }
  }, []);

  useEffect(() => {
    dispatch(getAllParentOrganisation({}));
  }, [dispatch]);

  useEffect(() => {
    if (orgId) {
      dispatch(getOrganisation(orgId)).then(({ payload }) => {
        const payloadData = payload?.data?.data?.[0];
        if (payloadData?._id) {
          const {
            name,
            abn,
            acn,
            address,
            email,
            complaintsEmail,
            phoneNumber,
            telephoneNumber,
            organisationsettings,
            parentOrganisation,
          } = payloadData;
          const row = payloadData;
          const aclSource = row?.acl ?? row;
          setOrganisationInfo({
            name: name ?? "",
            abn: abn ?? "",
            acn: acn ?? "",
            email: email ?? "",
            complaintsEmail: complaintsEmail ?? "",
            phoneNumber: phoneNumber ?? "",
            telephoneNumber: telephoneNumber ?? "",
            address: address ?? "",
            serviceInvoicePaidBy: organisationsettings?.[0]?.service_invoice_paid_by ?? "",
            parentOrganisation: parentOrganisation?.[0]?.name ?? "",
          });
          setAclInfo({
            number: aclSource?.number ?? "",
            name: aclSource?.name ?? "",
            abn: aclSource?.abn ?? "",
            complaintsEmail: aclSource?.complaintsEmail ?? "",
            complaintsPhoneNumber: aclSource?.complaintsPhoneNumber ?? "",
            complaintsTelephoneNumber: aclSource?.complaintsTelephoneNumber ?? "",
            complaintsAddress: aclSource?.complaintsAddress ?? "",
            privacyPolicyLink: aclSource?.privacyPolicyLink ?? "",
            lenders: Array.isArray(aclSource?.lenders) ? aclSource.lenders.slice(0, 6) : [],
          });
          setAclFieldErrors(INITIAL_ACL_FIELD_ERRORS);
        }
      });
    } else {
      setOrganisationInfo(INITIAL_ORG_INFO);
      setAclInfo(INITIAL_ACL_INFO);
      setAclFieldErrors(INITIAL_ACL_FIELD_ERRORS);
    }
  }, [orgId]);

  useEffect(() => {
    if (organisationDetails) {
      setOrganisationInfo((prev) => ({
        ...prev,
        name: organisationDetails?.EntityName ?? prev.name,
        abn: organisationDetails?.Abn ?? prev.abn,
        acn: organisationDetails?.Acn ?? prev.acn,
      }));
    }
  }, [organisationDetails]);

  const aclFormHasErrors = Object.values(aclFieldErrors).some(Boolean);

  const canSubmit = !!(
    organisationInfo.name &&
    organisationInfo.abn &&
    organisationInfo.serviceInvoicePaidBy &&
    organisationInfo.parentOrganisation &&
    !fieldErrors.name &&
    !fieldErrors.abn &&
    !fieldErrors.serviceInvoicePaidBy &&
    !aclFormHasErrors
  );

  const handle = {
    onChangeField: async (fieldName, value) => {
      let valid = true;

      if (!valid) {
        await validateField(fieldName, value);
        return;
      } else {
        setOrganisationInfo({
          ...organisationInfo,
          [fieldName]: value,
        });
        await validateField(fieldName, value);
      }
    },
    registerOrUpdateOrganisation: async () => {
      if (!canSubmit) return;

      const findOrg = organisationNameList?.find((org) => organisationInfo.parentOrganisation === org.name);
      const aclPayload = getAclPayload(aclInfo);

      const data = {
        ...organisationInfo,
        parentOrganisation: organisationInfo.parentOrganisation ? findOrg?.orgId : "",
        status: "active",
        ...(aclPayload ? { acl: aclPayload } : {}),
      };

      const action = orgId ? updateOrganisation({ ...data, _id: orgId }) : createOrganisation(data);
      const successMessage = orgId ? SUCCESS_MESSAGE.organisationUpdate : SUCCESS_MESSAGE.organisationCreate;

      dispatch(action).then(({ payload, error }) => {
        if (error) {
          dispatch(
            openSnackbar({ message: payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(openSnackbar({ message: payload?.data?.status_message || successMessage, intent: "success" }));

          setTimeout(() => {
            navigate("/organisation");
          }, 500);
        }
      });
    },
  };

  return (
    <>
      <Grid component="form" container spacing={2} sx={{ margin: "0 80px" }}>
        <Grid item xs={12}>
          <Typography
            level="title-lg"
            style={{
              letterSpacing: "-1.5px",
              marginBottom: "15px",
              textAlign: "center",
            }}
          >
            {orgId ? "Update" : "Add"} Organisation
          </Typography>
        </Grid>

        {gettingOrganisation ? (
          <Grid item xs={12}>
            <Loader height="50vh" />
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <OrganisationSearch
                organisationDetails={organisationDetails}
                setOrganisationDetails={setOrganisationDetails}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth sx={{ marginTop: "8px", marginBottom: "4px" }}>
                <InputLabel id="parent-org-label" required>
                  Parent Organisation
                </InputLabel>
                <Select
                  labelId="parent-org-label"
                  id="parent-org"
                  variant="filled"
                  value={organisationInfo.parentOrganisation ?? ""}
                  label="Parent Organisation"
                  required
                  onChange={(e) => handle.onChangeField("parentOrganisation", e.target.value)}
                >
                  {organisationNameList?.map((item) => (
                    <MenuItem value={item.name} key={item.orgId}>
                      {item.name.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                shrink
                type="text"
                name="name"
                required
                label="Organisation Name"
                autoComplete="Organisation Name"
                variant="filled"
                size="small"
                value={organisationInfo.name ?? ""}
                onChange={(e) => handle.onChangeField("name", e.target.value)}
                margin="dense"
                helperText={fieldErrors.name}
                error={!!fieldErrors.name}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                shrink
                type="text"
                name="abn"
                label="ABN"
                required
                autoComplete="ABN"
                variant="filled"
                size="small"
                value={organisationInfo.abn ?? ""}
                onChange={(e) => handle.onChangeField("abn", e.target.value)}
                margin="dense"
                helperText={fieldErrors.abn}
                error={!!fieldErrors.abn}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                shrink
                type="text"
                name="acn"
                label="ACN"
                autoComplete="ACN"
                variant="filled"
                size="small"
                value={organisationInfo.acn ?? ""}
                onChange={(e) => handle.onChangeField("acn", e.target.value)}
                margin="dense"
                helperText={fieldErrors.acn}
                error={!!fieldErrors.acn}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControl
                fullWidth
                sx={{ marginTop: "8px", marginBottom: "4px" }}
                error={!!fieldErrors.serviceInvoicePaidBy}
              >
                <InputLabel id="invoice-paid-by-label" required>
                  Invoice paid by
                </InputLabel>
                <Select
                  size="small"
                  variant="filled"
                  id="invoice-paid-by"
                  value={organisationInfo.serviceInvoicePaidBy ?? ""}
                  label="Invoice paid by"
                  onChange={(e) => handle.onChangeField("serviceInvoicePaidBy", e.target.value)}
                >
                  {["ORGANISATION", "USER"].map((item) => (
                    <MenuItem value={item} key={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.serviceInvoicePaidBy && (
                  <FormHelperText sx={{ color: "#d32f2f" }}>{fieldErrors.serviceInvoicePaidBy}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                shrink
                type="email"
                name="email"
                label="Email"
                autoComplete="email"
                variant="filled"
                size="small"
                value={organisationInfo.email ?? ""}
                onChange={(e) => handle.onChangeField("email", e.target.value)}
                margin="dense"
                helperText={fieldErrors.email}
                error={!!fieldErrors.email}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {orgAccessComponent.isOrgComplaintsEmail && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  shrink
                  type="email"
                  name="complaintsEmail"
                  label="Complaints Email"
                  autoComplete="newEmail"
                  variant="filled"
                  size="small"
                  value={organisationInfo.complaintsEmail ?? ""}
                  onChange={(e) => handle.onChangeField("complaintsEmail", e.target.value)}
                  margin="dense"
                  helperText={fieldErrors.complaintsEmail}
                  error={!!fieldErrors.complaintsEmail}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="text"
                name="phoneNumber"
                label="Mobile number"
                autoComplete="tel"
                variant="filled"
                size="small"
                value={organisationInfo.phoneNumber ?? ""}
                onChange={(e) => handle.onChangeField("phoneNumber", e.target.value)}
                margin="dense"
                helperText={fieldErrors.phoneNumber}
                error={!!fieldErrors.phoneNumber}
                inputProps={{ maxLength: 12 }}
                placeholder="0400123123"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="text"
                name="telephoneNumber"
                label="Telephone number"
                autoComplete="tel"
                variant="filled"
                size="small"
                value={organisationInfo.telephoneNumber ?? ""}
                onChange={(e) => handle.onChangeField("telephoneNumber", e.target.value)}
                margin="dense"
                helperText={fieldErrors.telephoneNumber}
                error={!!fieldErrors.telephoneNumber}
                inputProps={{ maxLength: 12 }}
                placeholder="0000000000"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                shrink
                type="text"
                name="address"
                label="Address"
                variant="filled"
                margin="dense"
                value={organisationInfo.address ?? ""}
                onChange={(e) => handle.onChangeField("address", e.target.value)}
                helperText={fieldErrors.address}
                error={!!fieldErrors.address}
                inputProps={{ maxLength: 200 }}
              />
            </Grid>

            <OrganisationAddAclSection
              aclInfo={aclInfo}
              setAclInfo={setAclInfo}
              aclFieldErrors={aclFieldErrors}
              setAclFieldErrors={setAclFieldErrors}
              orgAccessComponent={orgAccessComponent}
            />

            <Grid item xs={12}>
              <LoadingButton
                fullWidth
                loadingPosition="end"
                variant="contained"
                disabled={!canSubmit}
                color="secondary"
                style={{ margin: "20px 0 10px" }}
                onClick={handle.registerOrUpdateOrganisation}
              >
                {orgId ? "Update" : "Create"} Organisation
              </LoadingButton>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default OrganisationAdd;
